import classNames from "classnames";

import css from "./icon.module.scss";

import type { ReactNode } from "react";
import type { IconName } from "./iconNames";

export type { IconName };

type IconType = "regular" | "solid";

export type IconProps = {
  name: IconName;
  className?: string;
  type?: IconType;
  button?: boolean;
};

const ICONS: Record<string, ReactNode> = {
  backward: (
    <>
      <path d="m11 6-6 6 6 6V6Z" />
      <path d="m19 6-6 6 6 6V6Z" />
    </>
  ),
  backspace: (
    <>
      <path d="M20 5H9l-6 7 6 7h11z" />
      <path d="m12 9 5 6m0-6-5 6" />
    </>
  ),
  bars: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M10 21h4" />
    </>
  ),
  calculator: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 7h8" />
      <path d="M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01M8 19h.01M12 19h.01M16 19h.01" />
    </>
  ),
  check: <path d="m5 12 5 5L20 7" />,
  "chess-knight": (
    <>
      <path d="M8 20h10" />
      <path d="M9 16h8l-1.5-7.5L12 4 8 8l3 1-3 4z" />
      <path d="M11 7h.01" />
    </>
  ),
  "chevron-down": <path d="m6 9 6 6 6-6" />,
  "chevron-left": <path d="m15 18-6-6 6-6" />,
  "chevron-right": <path d="m9 18 6-6-6-6" />,
  "chevron-up": <path d="m18 15-6-6-6 6" />,
  cog: (
    <>
      <path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z" />
      <path d="m19.4 15 .3 2.2-2 1.2-1.8-1a8 8 0 0 1-1.8 1l-.4 2.1h-2.4l-.4-2.1a8 8 0 0 1-1.8-1l-1.8 1-2-1.2.3-2.2a8 8 0 0 1-.9-1.8L3 12l1-1.2a8 8 0 0 1 .9-1.8l-.3-2.2 2-1.2 1.8 1a8 8 0 0 1 1.8-1l.4-2.1h2.4l.4 2.1a8 8 0 0 1 1.8 1l1.8-1 2 1.2-.3 2.2a8 8 0 0 1 .9 1.8l1 1.2-1 1.2a8 8 0 0 1-.9 1.8Z" />
    </>
  ),
  "country-search-nofill": (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m16 16 5 5" />
      <path d="M7.5 10.5h6M10.5 7.5v6" />
    </>
  ),
  eraser: (
    <>
      <path d="m7 21-4-4L14 6a3 3 0 0 1 4 0l1 1a3 3 0 0 1 0 4L9 21z" />
      <path d="m12 8 6 6" />
      <path d="M3 21h18" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  "external-link": (
    <>
      <path d="M14 4h6v6" />
      <path d="m10 14 10-10" />
      <path d="M20 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4" />
    </>
  ),
  "eye-slash": (
    <>
      <path d="m3 3 18 18" />
      <path d="M10.6 10.6A3 3 0 0 0 13.4 13.4" />
      <path d="M9.9 5.3A9.7 9.7 0 0 1 12 5c6 0 10 7 10 7a17 17 0 0 1-3.2 3.9" />
      <path d="M6.5 6.8A17.5 17.5 0 0 0 2 12s4 7 10 7a9.7 9.7 0 0 0 4.2-1" />
    </>
  ),
  "game-board": (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 12h16M12 4v16" />
    </>
  ),
  forward: (
    <>
      <path d="m13 6 6 6-6 6V6Z" />
      <path d="m5 6 6 6-6 6V6Z" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
    </>
  ),
  "grip-vertical": (
    <>
      <path d="M9 5h.01M15 5h.01M9 12h.01M15 12h.01M9 19h.01M15 19h.01" />
    </>
  ),
  history: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10" width="14" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  lightbulb: (
    <>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M8.5 14.5A6 6 0 1 1 15.5 14.5c-.9.8-1.5 1.8-1.5 3h-4c0-1.2-.6-2.2-1.5-3Z" />
    </>
  ),
  "object-group": (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1" />
      <rect x="13" y="13" width="7" height="7" rx="1" />
      <path d="M8 11v5a2 2 0 0 0 2 2h3" />
    </>
  ),
  palette: (
    <>
      <path d="M12 3a9 9 0 0 0 0 18h1.5a2 2 0 0 0 1.5-3.3 1.5 1.5 0 0 1 1.1-2.5H18a3 3 0 0 0 3-3A9 9 0 0 0 12 3Z" />
      <path d="M7.5 11h.01M9 7.5h.01M13 7.5h.01M16.5 10.5h.01" />
    </>
  ),
  "paper-plane": <path d="m22 2-7 20-4-9-9-4zM22 2 11 13" />,
  pause: (
    <>
      <path d="M8 5v14" />
      <path d="M16 5v14" />
    </>
  ),
  pen: (
    <>
      <path d="m16 3 5 5L8 21H3v-5z" />
      <path d="m14 5 5 5" />
    </>
  ),
  pencil: (
    <>
      <path d="m16 3 5 5L8 21H3v-5z" />
      <path d="m14 5 5 5" />
    </>
  ),
  "question-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.7 2.7 0 0 1 5.1 1.3c0 2-2.6 2.4-2.6 4.2" />
      <path d="M12 18h.01" />
    </>
  ),
  redo: (
    <>
      <path d="M21 7v6h-6" />
      <path d="M20 13a8 8 0 1 1-2.3-5.7L21 10" />
    </>
  ),
  route: (
    <>
      <circle cx="6" cy="6" r="2" />
      <circle cx="18" cy="18" r="2" />
      <path d="M8 6h4a4 4 0 0 1 0 8H9a3 3 0 0 0 0 6h7" />
    </>
  ),
  "sliders-h": (
    <>
      <path d="M4 7h16M4 17h16" />
      <circle cx="9" cy="7" r="2" />
      <circle cx="15" cy="17" r="2" />
    </>
  ),
  "spell-check": (
    <>
      <path d="m4 16 5-12 5 12" />
      <path d="M6 11h6" />
      <path d="m14 17 3 3 5-6" />
    </>
  ),
  spinner: (
    <>
      <path d="M21 12a9 9 0 1 1-6.2-8.6" />
    </>
  ),
  square: <rect x="5" y="5" width="14" height="14" rx="2" />,
  star: <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2-5.5-2.9-5.5 2.9 1-6.2L3 9.6l6.2-.9z" />,
  th: (
    <>
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="4" y="14" width="6" height="6" rx="1" />
      <rect x="14" y="14" width="6" height="6" rx="1" />
    </>
  ),
  "thumbs-down": (
    <>
      <path d="M7 3v12" />
      <path d="M7 14H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3l6 1h4a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-3l1 5a2 2 0 0 1-2 3h-1z" />
    </>
  ),
  "thumbs-up": (
    <>
      <path d="M7 21V9" />
      <path d="M7 10h3l-1-5a2 2 0 0 1 2-3h1l5 7h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-4l-6 1z" />
    </>
  ),
  times: (
    <>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </>
  ),
  trophy: (
    <>
      <path d="M8 21h8" />
      <path d="M12 17v4" />
      <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
      <path d="M7 6H4a3 3 0 0 0 3 3M17 6h3a3 3 0 0 1-3 3" />
    </>
  ),
  undo: (
    <>
      <path d="M3 7v6h6" />
      <path d="M4 13a8 8 0 1 0 2.3-5.7L3 10" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  "user-plus": (
    <>
      <circle cx="10" cy="8" r="4" />
      <path d="M3 21a7 7 0 0 1 14 0" />
      <path d="M19 8v6M16 11h6" />
    </>
  ),
  "wand-magic": (
    <>
      <path d="m4 20 10-10" />
      <path d="m13 4 7 7" />
      <path d="m15 2 2 2M20 7l2 2M7 2l1 3M2 7l3 1M16 17l1 3" />
    </>
  ),
};

const FILLED = new Set(["square", "star"]);

export function Icon({ name, className, button, type = "regular" }: IconProps) {
  const icon = ICONS[name] ?? ICONS["question-circle"];
  const filled = type === "solid" && FILLED.has(name);

  return (
    <svg
      aria-hidden="true"
      className={classNames(css.container, button && css.button, className)}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      focusable="false"
    >
      {icon}
    </svg>
  );
}
